import type { TranslationKeys } from '../types';

const fr: TranslationKeys = {
  nav: {
    home: 'Accueil',
    blockExplorer: 'Explorateur',
    swap: 'Échange',
    exchange: 'Échange cross-chain',
    docs: 'Docs',
    more: 'Plus',
    close: 'Fermer',
    bridge: 'Pont',
  },
  bridge: {
    title: 'Pont Igra',
    subtitle: 'Pont KAS entre Kaspa et Igra (chaîne {chainId})',
    tabDeposit: 'Déposer KAS',
    tabDepositSub: 'KAS → iKAS',
    tabWithdraw: 'Retirer KAS',
    tabWithdrawSub: 'iKAS → KAS',
    chainKaspa: 'Kaspa',
    chainEvm: 'EVM (Igra)',
    useWallet: 'Utiliser le portefeuille',
    gate: {
      installTitle: 'Connecter KasWare',
      installDesc:
        "Les transactions du pont sont signées entièrement dans KasWare. Installez l'extension ou ouvrez cette page dans l'application Telegram KasWare.",
      disconnectedTitle: 'Portefeuille déconnecté',
      connectWallet: 'Connecter le portefeuille',
      connectTitle: 'Connecter {chain}',
      connectDesc: 'Autorisez le compte {chain} dans KasWare pour continuer.',
      connecting: 'Connexion...',
      connectChain: 'Connecter {chain}',
    },
    deposit: {
      youSend: 'Vous envoyez (Kaspa L1)',
      youReceive: 'Vous recevez (Igra L2)',
      recipientLabel: 'Recevez les iKAS à cette adresse Igra',
      fromWallet: '(du portefeuille)',
      badAddress: "Saisissez une adresse EVM valide — une adresse avec des majuscules doit passer la somme de contrôle EIP-55.",
      connectEvm: 'Connecter EVM pour le remplissage automatique',
      connectingEvm: 'Connexion à EVM...',
      infoMin: 'Dépôt minimum : {min} KAS.',
      infoMining:
        "L'identifiant de transaction est miné dans votre navigateur pour commencer par 97b1 — quelques secondes généralement.",
      infoMint: "Les iKAS sont frappés après que le séquenceur Igra confirme l'Entry sur Kaspa.",
      phaseConnecting: 'Connexion au réseau Kaspa...',
      phaseFetching: 'Récupération de vos UTXO...',
      phaseMining: "Recherche d'un identifiant de transaction valide... ({tries} essais)",
      phaseSigning: 'En attente de votre signature dans KasWare...',
      phaseBroadcasting: "Diffusion de la transaction d'entrée...",
      cancelMining: 'Annuler le minage',
      successTitle: 'Dépôt envoyé',
      successMined: 'Identifiant valide trouvé après {tries} essais (nonce {nonce}).',
      kaspaTx: 'Transaction Kaspa :',
      prefixWarning:
        "L'identifiant de transaction diffusé ne commence plus par 97b1. Igra pourrait ne pas créditer ce dépôt — conservez l'identifiant et contactez le support.",
      newDeposit: 'Nouveau dépôt',
      cta: 'Déposer des KAS sur Igra',
      ctaAmount: 'Déposer {amount} KAS sur Igra',
    },
    withdraw: {
      youBurn: 'Vous brûlez (Igra L2)',
      paidOut: 'Versé sur (Kaspa L1)',
      payoutLabel: 'Adresse de versement KAS',
      infoLimits: 'Limites : {min} – {max} iKAS par retrait.',
      infoFeeZero: 'Frais de protocole : actuellement 0.',
      infoFeeQuoted: 'Frais de protocole : indiqués à la demande.',
      infoRate: '1 iKAS brûlé donne exactement 1 KAS versé (sans écart).',
      warningPrefix: 'Les retraits',
      warningStrong: 'ne sont pas instantanés',
      warningRest:
        "les iKAS sont brûlés sur Igra, puis un comité multi-signatures libère les KAS sur Kaspa après examen de la demande. N'envoyez que des montants que vous pouvez attendre.",
      checking: 'Vérification...',
      modalTitle: 'Confirmer le retrait',
      modalBurn: 'Vous brûlez',
      modalFee: 'Frais de protocole',
      modalPayout: 'KAS versés à',
      modalAck:
        "Je comprends que le versement est manuel, revu par un comité multi-signatures, et n'est pas instantané.",
      modalConfirm: 'Confirmer et brûler les iKAS',
      modalBack: 'Retour',
      successTitle: 'Demande de sortie confirmée sur Igra',
      requestId: 'Id de demande de sortie : {id}',
      igraTx: 'Transaction Igra :',
      successPayout: 'Les KAS seront envoyés à :',
      successNote: 'Les KAS seront versés à votre adresse après la revue multi-signatures.',
      newWithdrawal: 'Nouveau retrait',
      requesting: 'Demande de retrait...',
      cta: 'Brûler iKAS et demander KAS',
      ctaAmount: 'Brûler {amount} iKAS et demander KAS',
    },
  },
  home: {
    tagline: 'KasWare Wallet',
    hero: {
      subtitle:
        'Un seul portefeuille auto-dépositaire pour TON, Kaspa et les chaînes EVM — vos clés restent sur votre appareil.',
      chainsLabel: 'Réseaux pris en charge',
      ctaDownload: 'Ajouter à Chrome',
      ctaTelegram: 'Ouvrir dans Telegram',
      note: 'Gratuit, sans inscription ni e-mail.',
    },
    pillars: {
      security: {
        title: 'Sécurisé par conception',
        desc: 'Les clés sont générées et chiffrées sur votre appareil. Chaque transaction est analysée et affichée en entier avant signature.',
      },
      convenience: {
        title: 'Tout au même endroit',
        desc: 'Conservez, envoyez, échangez et faites des ponts entre TON, Kaspa et EVM sans multiplier les portefeuilles.',
      },
      friendly: {
        title: 'Pensé pour les gens',
        desc: 'Messages en langage clair, adresses lisibles, thèmes clair et sombre, et sept langues incluses.',
      },
      reliable: {
        title: 'Fiable',
        desc: 'Historique vérifiable, plusieurs points de connexion réseau et un portefeuille qui reste utilisable si un fournisseur tombe.',
      },
    },
    content: {
      downloadTitle: 'Télécharger',
      securityTitle: 'Pourquoi vos fonds restent les vôtres',
      p1: 'KasWare est un portefeuille auto-dépositaire pour Ton, Kaspa et EVM.',
      p2: "Nous n'avons jamais accès à vos fonds. Vos clés privées sont chiffrées sur votre appareil par votre mot de passe et ne sont partagées avec personne.",
      p3: 'Tous les comptes sont dérivés de manière déterministe à partir de votre phrase de récupération secrète, vous donnant pleine propriété et contrôle.',
    },
  },
  about: {
    contact: 'Contactez-nous',
    contacts: {
      twitter: 'Twitter',
      telegram: 'Telegram',
      discord: 'Discord',
      email: 'Email',
    },
  },
  footer: {
    privacy: 'Politique de confidentialité',
    copyright: '© {year} Flying Rabbit Tech Ltd',
  },
  explorer: {
    title: 'Explorateur de transactions',
    subtitle: 'Recherchez les enregistrements de transactions Kaspa et KRC-20 par adresse',
    timeRange: 'Plage horaire :',
    to: 'à',
    clear: 'Effacer',
    address: 'Adresse :',
    kasTab: 'Transferts KAS',
    krc20Tab: 'Opérations KRC-20',
    enterAddress: 'Entrez une adresse Kaspa pour voir les transactions',
    loading: 'Chargement...',
    search: {
      placeholder: "Entrez l'adresse Kaspa (kaspa:q...)",
      button: 'Rechercher',
    },
    tx: {
      loading: 'Chargement des transactions...',
      empty: 'Aucune transaction trouvée pour cette adresse',
      emptyInRange: 'Aucune transaction trouvée pour cette adresse dans la plage horaire sélectionnée',
      failed: 'Échec du chargement des transactions : {error}',
      retry: 'Réessayer',
    },
    krc20: {
      loading: 'Chargement des opérations KRC-20...',
      empty: 'Aucune opération KRC-20 trouvée pour cette adresse',
      emptyInRange: 'Aucune opération KRC-20 trouvée pour cette adresse dans la plage horaire sélectionnée',
      failed: 'Échec du chargement des opérations KRC-20 : {error}',
      retry: 'Réessayer',
    },
  },
  languageNames: {
    en: 'EN',
    id: 'ID',
    fr: 'FR',
    zh_CN: '简体中文',
    zh_TW: '繁體中文',
    es: 'ES',
    th: 'TH',
  },
};

export default fr;
